import type { Camera, InstancedMesh } from 'three';
import { Matrix4 } from 'three';
import type { Animated } from '../types';
import type { ShipTier } from './hullParts';
import { writeShipPose, type ShipInstance } from './shipMotion';

/*
 * The fleet's distance tiers (docs/plans/ships.md 4). A formed ship's hull and standing rig are
 * drawn from one instanced body per type and tier: far beyond 250 m, mid within it, near within
 * 60 m, with some room before a ship leaves a tier so it does not flicker across the line. Its
 * running rig, pennants and smoke stay in its group's mesh at every distance. The tiers follow the
 * camera of the last frame drawn; while the painter view shows, every ship is far. An open boat's
 * body has no far mesh: its group draws it far, and while its body draws it its group slot is
 * folded to nothing (openBoat.ts).
 */

export const TIER_REACH = {
  near: { enter: 60, leave: 66 },
  mid: { enter: 250, leave: 270 },
} as const;

const TIER_ORDER: readonly ShipTier[] = ['far', 'mid', 'near'];
/* A group slot folded to a point draws no pixel. */
const NOTHING = new Matrix4().makeScale(0, 0, 0);

/** The tier a ship `distance` metres from the camera is drawn in, having been drawn in `previous`. */
export function tierOf(distance: number, previous: ShipTier): ShipTier {
  if (distance < TIER_REACH.near.enter) return 'near';
  if (previous === 'near' && distance <= TIER_REACH.near.leave) return 'near';
  if (distance < TIER_REACH.mid.enter) return 'mid';
  if (previous !== 'far' && distance <= TIER_REACH.mid.leave) return 'mid';
  return 'far';
}

/** A ship of the fleet: its pose, outline id, its slot in its group's mesh and its body, or -1. */
export interface FleetShip {
  readonly instance: ShipInstance;
  readonly edgeId: number;
  readonly group: number;
  readonly groupSlot: number;
  readonly body: number;
}

/** One instanced body per tier it draws, each with an `aInstanceEdgeId` attribute as long as its
    capacity; a ship in a tier its body does not draw is drawn by its group. */
export interface FleetBody {
  readonly meshes: Readonly<Partial<Record<ShipTier, InstancedMesh>>>;
}

export interface FleetLod {
  readonly animated: Animated;
  /** Records the camera a frame is drawn with; every fleet mesh calls it just before it draws. */
  sense(camera: Camera): void;
}

interface Slots {
  readonly tiers: Uint8Array;
  readonly bodySlots: Int32Array;
  readonly eye: Float64Array;
  seen: boolean;
  placed: boolean;
}

function distanceTo(eye: Float64Array, ship: FleetShip): number {
  const { baseX, baseY, baseZ } = ship.instance;
  return Math.hypot(baseX - eye[0]!, baseY - eye[1]!, baseZ - eye[2]!);
}

/* New tiers for every ship with a body; whether any ship changed tier. */
function retier(ships: readonly FleetShip[], slots: Slots, painter: boolean): boolean {
  let changed = false;
  for (let i = 0; i < ships.length; i++) {
    const ship = ships[i]!;
    if (ship.body < 0) continue;
    const before = TIER_ORDER[slots.tiers[i]!]!;
    const after = painter || !slots.seen ? 'far' : tierOf(distanceTo(slots.eye, ship), before);
    if (after === before) continue;
    slots.tiers[i] = TIER_ORDER.indexOf(after);
    changed = true;
  }
  return changed;
}

/* Every body draws its ships of each tier it has in fleet order, from its first instance on; a
   ship in a tier its body lacks takes no slot. */
function place(ships: readonly FleetShip[], bodies: readonly FleetBody[], slots: Slots): void {
  const counts = bodies.map(() => new Int32Array(TIER_ORDER.length));
  for (let i = 0; i < ships.length; i++) {
    const ship = ships[i]!;
    slots.bodySlots[i] = -1;
    if (ship.body < 0) continue;
    const tier = slots.tiers[i]!;
    const mesh = bodies[ship.body]!.meshes[TIER_ORDER[tier]!];
    if (!mesh) continue;
    const slot = counts[ship.body]![tier]!++;
    slots.bodySlots[i] = slot;
    const ids = mesh.geometry.getAttribute('aInstanceEdgeId');
    (ids.array as Float32Array)[slot] = ship.edgeId;
  }
  bodies.forEach((body, b) => {
    TIER_ORDER.forEach((tier, t) => {
      const mesh = body.meshes[tier];
      if (!mesh) return;
      mesh.count = counts[b]![t]!;
      mesh.visible = mesh.count > 0;
      mesh.boundingSphere = null;
      mesh.geometry.getAttribute('aInstanceEdgeId').needsUpdate = true;
    });
  });
}

/* A ship whose body lacks a far mesh is drawn by its body or by its group, never both. */
function groupYields(bodies: readonly FleetBody[], ship: FleetShip, bodySlot: number): boolean {
  return ship.body >= 0 && bodySlot >= 0 && !bodies[ship.body]!.meshes.far;
}

function pose(
  ships: readonly FleetShip[],
  groups: readonly InstancedMesh[],
  bodies: readonly FleetBody[],
  slots: Slots,
  matrix: Matrix4,
  timeSeconds: number
): void {
  for (let i = 0; i < ships.length; i++) {
    const ship = ships[i]!;
    const bodySlot = slots.bodySlots[i]!;
    writeShipPose(matrix.elements, ship.instance, timeSeconds);
    groups[ship.group]!.setMatrixAt(
      ship.groupSlot,
      groupYields(bodies, ship, bodySlot) ? NOTHING : matrix
    );
    if (bodySlot < 0) continue;
    bodies[ship.body]!.meshes[TIER_ORDER[slots.tiers[i]!]!]!.setMatrixAt(bodySlot, matrix);
  }
  for (const group of groups) group.instanceMatrix.needsUpdate = true;
  for (const body of bodies)
    for (const tier of TIER_ORDER) {
      const mesh = body.meshes[tier];
      if (mesh) mesh.instanceMatrix.needsUpdate = true;
    }
}

/**
 * Draws the fleet: each frame it sets every ship in its tier's body and writes the ship's rocking
 * pose into its group's mesh and its body. `painterAmount` reads how far the painter view shows.
 */
export function createFleetLod(
  ships: readonly FleetShip[],
  groups: readonly InstancedMesh[],
  bodies: readonly FleetBody[],
  painterAmount: () => number
): FleetLod {
  const slots: Slots = {
    tiers: new Uint8Array(ships.length),
    bodySlots: new Int32Array(ships.length),
    eye: new Float64Array(3),
    seen: false,
    placed: false,
  };
  const matrix = new Matrix4();
  const update = (timeSeconds: number): void => {
    const changed = retier(ships, slots, painterAmount() > 0);
    if (changed || !slots.placed) place(ships, bodies, slots);
    slots.placed = true;
    pose(ships, groups, bodies, slots, matrix, timeSeconds);
  };
  const sense = (camera: Camera): void => {
    const e = camera.matrixWorld.elements;
    slots.eye[0] = e[12];
    slots.eye[1] = e[13];
    slots.eye[2] = e[14];
    slots.seen = true;
  };
  return { animated: { id: 'ships', update }, sense };
}
